import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Button from '../Button';
import Link from '../Link';
import TranslationProvider from '../../providers/TranslationProvider';
import { getPageContext } from '../../utils';
import isFeatureEnabled from '../../utils/isFeatureEnabled';
import {
  RESY_WEB_ENABLE_WIDGET_MENU_UPDATES,
  RESY_WEB_ENABLE_WIDGET_UPDATES_IMPACTING_JCE,
} from '../../constants/optimizelyExperiments';

import './ProfileMenu.scss';

const CLICK_LINK_EVENT = 'click link';
const RESERVATION_MENU_LINK_NAME = 'Reservations';
const CONCIERGE_RESERVATION_MENU_LINK_NAME = 'Concierge Reservations';
const PROFILE_MENU_LINK_NAME = 'Profile';
const PAYMENT_METHODS_MENU_LINK_NAME = 'Payment Methods';
const COMMUNICATION_MENU_LINK_NAME = 'Communication';
const HELP_FEEDBACK_MENU_LINK_NAME = 'Help & Feedback';

const isThirdPartyPartnerWidget = () => {
  const refererDomain = window?.document?.referrer;
  if (refererDomain && refererDomain.length > 0) {
    return !refererDomain.includes('resy.com');
  }
  return false;
};

const isResyHostedWidget = () => {
  const refererDomain = window?.document?.referrer;
  return !!refererDomain && refererDomain.includes('resy.com');
};

const shouldRemoveMenuItem = (
  itemName,
  experiments,
  isFeatureEnabledFn
) => (
  isFeatureEnabledFn(experiments, RESY_WEB_ENABLE_WIDGET_UPDATES_IMPACTING_JCE)
  && isThirdPartyPartnerWidget()
  && (itemName === PROFILE_MENU_LINK_NAME || itemName === PAYMENT_METHODS_MENU_LINK_NAME)
)
|| (
  isFeatureEnabledFn(experiments, RESY_WEB_ENABLE_WIDGET_MENU_UPDATES)
  && [
    COMMUNICATION_MENU_LINK_NAME,
    HELP_FEEDBACK_MENU_LINK_NAME,
  ].includes(itemName)
  && isThirdPartyPartnerWidget()
);

const shouldRemoveWebMenuItem = (
  itemName,
  experiments,
  isFeatureEnabledFn
) => (
  isFeatureEnabledFn(experiments, RESY_WEB_ENABLE_WIDGET_UPDATES_IMPACTING_JCE)
  && isResyHostedWidget()
  && [
    PROFILE_MENU_LINK_NAME,
    PAYMENT_METHODS_MENU_LINK_NAME,
    COMMUNICATION_MENU_LINK_NAME,
    HELP_FEEDBACK_MENU_LINK_NAME,
  ].includes(itemName)
);

const ProfileMenuLinks = ({
  Analytics,
  disabled,
  experiments,
  isConcierge,
  items,
  onLogout,
  onToggle,
}) => {
  const context = getPageContext(window?.location?.pathname || '');
  const isResyDomain = window?.location?.hostname?.endsWith('resy.com') || false;
  const widgetSurface = isResyDomain ? 'resy widget' : 'partner widget';

  const sendAnalyticsEvent = (eventName, value) => Analytics.event(eventName, {
    context,
    value,
    widget_surface: widgetSurface,
  });

  const handleMenuClick = (item, event) => {
    if (onToggle) onToggle(event);
    sendAnalyticsEvent(CLICK_LINK_EVENT, item.name);
  };

  const handleLogoutClick = async () => {
    onLogout();
    await sendAnalyticsEvent(CLICK_LINK_EVENT, 'signout');
  };

  const checkIfLinkNeedsUpdated = (menuName) => {
    const isFlagEnabled = isFeatureEnabled(experiments, RESY_WEB_ENABLE_WIDGET_UPDATES_IMPACTING_JCE);
    const isEligibleMenu = [RESERVATION_MENU_LINK_NAME, CONCIERGE_RESERVATION_MENU_LINK_NAME].includes(menuName);

    return isFlagEnabled && isEligibleMenu && isThirdPartyPartnerWidget();
  };

  const getHref = (menuName, path, matcher) => {
    if (checkIfLinkNeedsUpdated(menuName)) {
      return `https://resy.com/${matcher}`;
    }

    return path;
  };

  const getTarget = (menuName) => {
    if (checkIfLinkNeedsUpdated(menuName)) {
      return '_blank';
    }

    return '_self';
  };

  const getMenu = (menu) => (
    <ul>
      {menu
        .filter((i) => !i.isConcierge || !!i.isConcierge === !!isConcierge)
        .filter((item) => !shouldRemoveMenuItem(item.name, experiments, isFeatureEnabled))
        .filter((item) => !shouldRemoveWebMenuItem(item.name, experiments, isFeatureEnabled))
        .map(
          (item) => (
            <li key={item.name}>
              <Link
                className={classnames({ active: `/${item.activeMatcher}` === window.location.path })}
                data-test-id={`profile_menu-link-${item.name.replace(' ', '_').toLowerCase()}`}
                disabled={disabled}
                target={getTarget(item.name)}
                href={getHref(item.name, item.path, item.activeMatcher)}
                onClick={(e) => handleMenuClick(item, e)}
                variant="menu"
              >
                <TranslationProvider translationKey={item.translationKey}>
                  {item.name}
                </TranslationProvider>
              </Link>
            </li>
          )
        )}
    </ul>
  );

  return (
    <>
      {getMenu(items)}
      <hr />
      <Button
        className="ProfileMenu__Menu--signout"
        data-test-id="profile_menu-link-sign_out"
        disabled={disabled}
        onClick={handleLogoutClick}
        variant="link"
      >
        <TranslationProvider translationKey="signout">
          Sign Out
        </TranslationProvider>
      </Button>
    </>
  );
};

ProfileMenuLinks.propTypes = {
  Analytics: PropTypes.object,
  disabled: PropTypes.bool,
  experiments: PropTypes.shape({}).isRequired,
  isConcierge: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      path: PropTypes.string,
      translationKey: PropTypes.string,
      activeMatcher: PropTypes.string,
      isConcierge: PropTypes.bool,
    })
  ),
  onLogout: PropTypes.func.isRequired,
  onToggle: PropTypes.func,
};

ProfileMenuLinks.defaultProps = {
  Analytics: null,
  disabled: false,
  isConcierge: false,
  items: [],
  onToggle: () => {},
};

export { shouldRemoveMenuItem };
export default ProfileMenuLinks;
